
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiChevronDown,
  FiSearch,
  FiSliders,
} from "react-icons/fi";
import { useFetchAllCategoryQuery } from "../../store/api/ProductSlice";

const PURPLE = "#7027df";
const MAX_PRICE = 200;
const LOG_PREFIX = "[ShopSidebar]";

function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="border-b border-[#eeeaf3] py-3">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
      >
        <span className="text-[11px] font-bold text-[#292431]">
          {title}
        </span>

        <FiChevronDown
          size={13}
          className={`text-[#8e8798] transition-transform ${
            open ? "" : "-rotate-90"
          }`}
        />
      </button>

      {open && <div className="mt-2.5">{children}</div>}
    </section>
  );
}

function CheckOption({ label, checked, onChange, count }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 py-[3px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-[13px] w-[13px] shrink-0 cursor-pointer accent-[#7027df]"
      />

      <span className="min-w-0 flex-1 truncate text-[10px] text-[#5f5869]">
        {label}
      </span>

      {count != null && (
        <span className="text-[9px] text-[#a29baa]">{count}</span>
      )}
    </label>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <FiSearch
        size={11}
        className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9a93a3]"
      />

      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded border border-[#eeeaf3] py-1.5 pl-7 pr-2 text-[10px] outline-none focus:border-[#7027df]"
      />
    </div>
  );
}

function normalizeText(value) {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.flatMap(normalizeText);
  }

  if (typeof value === "object") {
    if (value.value != null) return normalizeText(value.value);
    if (value.name != null) return normalizeText(value.name);
    if (value.title != null) return normalizeText(value.title);
    return [];
  }

  const text = String(value).trim();
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);

    if (Array.isArray(parsed)) {
      return normalizeText(parsed);
    }
  } catch {
    // Ordinary text.
  }

  return [text];
}

function uniqueStrings(values) {
  const map = new Map();

  values.forEach((value) => {
    const name = String(value || "").trim();

    if (name && !map.has(name.toLowerCase())) {
      map.set(name.toLowerCase(), name);
    }
  });

  return Array.from(map.values()).sort((a, b) =>
    a.localeCompare(b)
  );
}

function uniqueSellers(values) {
  const map = new Map();

  values.forEach((seller) => {
    if (!seller.id || !seller.name) return;

    const id = String(seller.id);

    if (!map.has(id)) {
      map.set(id, {
        id,
        name: String(seller.name).trim(),
      });
    }
  });

  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

function ShopSidebar({ products = [] }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: categoryData = [],
    isLoading: categoriesLoading,
    error: categoryError,
  } = useFetchAllCategoryQuery();

  const [brandSearch, setBrandSearch] = useState("");
  const [sellerSearch, setSellerSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [flavorSearch, setFlavorSearch] = useState("");

  const [productNameSearch, setProductNameSearch] = useState(
    searchParams.get("productName") || ""
  );

  const selected = (key) => searchParams.getAll(key).filter(Boolean);

  const minPrice = Number(searchParams.get("minPrice") || 0);
  const maxPrice = Number(searchParams.get("maxPrice") || MAX_PRICE);

  const [priceDraft, setPriceDraft] = useState({
    min: minPrice,
    max: maxPrice,
  });

  // Keep Product Name input synchronized with URL.
  useEffect(() => {
    setProductNameSearch(searchParams.get("productName") || "");
  }, [searchParams]);

  // Keep price inputs synchronized with URL.
  useEffect(() => {
    setPriceDraft({
      min: minPrice,
      max: maxPrice,
    });
  }, [minPrice, maxPrice]);

  // Normalize categories returned by the category API.
  const categories = useMemo(() => {
    const items = Array.isArray(categoryData)
      ? categoryData
      : categoryData?.categories || categoryData?.data || [];

    if (!Array.isArray(items)) return [];

    return items
      .map((item) => ({
        name: String(
          item.name || item.parent || item.title || ""
        ).trim(),
        count: item.count ?? item.productCount,
      }))
      .filter((item) => item.name);
  }, [categoryData]);

  const filteredCategories = useMemo(
    () =>
      categories.filter((item) =>
        item.name.toLowerCase().includes(categorySearch.toLowerCase())
      ),
    [categories, categorySearch]
  );

  // Brand is a separate filter parameter, but its value is searched in title.
  // Build brand options from the first word of each product title.
  const brands = useMemo(() => {
    const values = products.flatMap((product) => {
      const title = String(product.title || "").trim();

      if (!title) return [];

      // Avoid treating the entire product title as the brand.
      // Replace this extraction with a dedicated brand source if available.
      return [title.split(/\s+/)[0]];
    });

    return uniqueStrings(values);
  }, [products]);

  // Extract Flavour / Flavours values from product attributes.
  const flavors = useMemo(() => {
    const values = products.flatMap((product) => {
      const attributes = Array.isArray(product.attributes)
        ? product.attributes
        : [];

      return attributes
        .filter((attribute) =>
          /^flavou?rs?$/i.test(
            String(attribute.name || "").trim()
          )
        )
        .flatMap((attribute) => normalizeText(attribute.value));
    });

    return uniqueStrings(values);
  }, [products]);

  // Seller values come from the populated author object.


const sellers = useMemo(() => {
  const values = (Array.isArray(products) ? products : []).flatMap(
    (product) => {
      const author = product?.author;

      if (!author || typeof author !== "object") {
        return [];
      }

      const id = author._id || author.id;

      // First name first; business name is only a fallback.
      const name =
        String(author.firstName || "").trim() ||
        String(author.businessName || "").trim() ||
        [author.lastName].filter(Boolean).join(" ").trim() ||
        String(author.email || "").trim();

      if (!id || !name) {
        return [];
      }

      return [{ id: String(id), name }];
    }
  );

  return uniqueSellers(values);
}, [products]);


  const filteredBrands = useMemo(
    () =>
      brands.filter((item) =>
        item.toLowerCase().includes(brandSearch.toLowerCase())
      ),
    [brands, brandSearch]
  );

  const filteredFlavors = useMemo(
    () =>
      flavors.filter((item) =>
        item.toLowerCase().includes(flavorSearch.toLowerCase())
      ),
    [flavors, flavorSearch]
  );

  const filteredSellers = useMemo(
    () =>
      sellers.filter((item) =>
        item.name.toLowerCase().includes(sellerSearch.toLowerCase())
      ),
    [sellers, sellerSearch]
  );

  // Update URL parameters and reset pagination.
  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      next.delete(key);

      if (Array.isArray(value)) {
        value
          .filter((item) => item !== "" && item != null)
          .forEach((item) => next.append(key, String(item)));
      } else if (value !== "" && value != null) {
        next.set(key, String(value));
      }
    });

    next.delete("page");

    console.log(`${LOG_PREFIX} Updated filters:`, next.toString());
    setSearchParams(next);
  };

  const toggleOption = (key, value) => {
    const stringValue = String(value);
    const values = selected(key);

    const nextValues = values.includes(stringValue)
      ? values.filter((item) => item !== stringValue)
      : [...values, stringValue];

    updateParams({ [key]: nextValues });
  };

  // Product Name filter.
  const applyProductName = () => {
    updateParams({
      productName: productNameSearch.trim(),
    });
  };

  // Apply price range.
  const commitPrice = () => {
    const min = Math.max(0, Number(priceDraft.min) || 0);
    const max = Math.max(min, Number(priceDraft.max) || MAX_PRICE);

    updateParams({
      minPrice: min > 0 ? min : "",
      maxPrice: max < MAX_PRICE ? max : "",
    });
  };

  // Preserve general search and Product Name when clearing sidebar filters.
  const clearFilters = () => {
    const next = new URLSearchParams();

    const searchTerm = searchParams.get("searchTerm");
    const productName = searchParams.get("productName");

    if (searchTerm) next.set("searchTerm", searchTerm);
    if (productName) next.set("productName", productName);

    setSearchParams(next);

    setBrandSearch("");
    setSellerSearch("");
    setCategorySearch("");
    setFlavorSearch("");
    setPriceDraft({ min: 0, max: MAX_PRICE });
  };

  const hasFilters = [
    "category",
    "productName",
    "minPrice",
    "maxPrice",
    "brand",
    "flavor",
    "seller",
    "inStock",
    "onSale",
  ].some((key) => searchParams.has(key));

  return (
    <aside className="w-full rounded-lg border border-[#eeeaf3] bg-white p-3">
      {/* Header */}
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FiSliders size={14} color={PURPLE} />
          <h2 className="text-[13px] font-bold text-[#292431]">
            Filters
          </h2>
        </div>

        <button
          type="button"
          onClick={clearFilters}
          className="text-[10px] font-semibold text-[#7027df] hover:underline"
        >
          Clear all
        </button>
      </div>

      {/* Category */}
      <FilterSection title="Category">
        <div className="mb-2">
          <SearchInput
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            placeholder="Search categories..."
          />
        </div>

        <div className="max-h-48 space-y-0.5 overflow-y-auto">
          {categoriesLoading ? (
            <p className="text-[10px] text-[#8e8798]">
              Loading categories...
            </p>
          ) : filteredCategories.length ? (
            filteredCategories.map((item) => (
              <CheckOption
                key={item.name}
                label={item.name}
                count={item.count}
                checked={selected("category").includes(item.name)}
                onChange={() => toggleOption("category", item.name)}
              />
            ))
          ) : (
            <p className="text-[10px] text-[#8e8798]">
              {categoryError
                ? "Unable to load categories."
                : "No categories found."}
            </p>
          )}
        </div>
      </FilterSection>

      {/* Product Name */}
      <FilterSection title="Product Name">
        <input
          type="text"
          value={productNameSearch}
          onChange={(e) => setProductNameSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyProductName();
            }
          }}
          placeholder="Search product name..."
          className="w-full rounded border border-[#eeeaf3] px-2 py-1.5 text-[10px] outline-none focus:border-[#7027df]"
        />

        <button
          type="button"
          onClick={applyProductName}
          className="mt-2 w-full rounded bg-[#7027df] py-1.5 text-[10px] font-semibold text-white hover:bg-[#5920b5]"
        >
          Apply product name
        </button>
      </FilterSection>

      {/* Price */}
      <FilterSection title="Price Range">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={priceDraft.min}
            onChange={(e) =>
              setPriceDraft((prev) => ({
                ...prev,
                min: e.target.value,
              }))
            }
            className="w-full min-w-0 rounded border border-[#eeeaf3] px-1.5 py-1 text-[10px] outline-none focus:border-[#7027df]"
            aria-label="Minimum price"
          />

          <span className="text-[10px] text-[#9a93a3]">to</span>

          <input
            type="number"
            min="0"
            value={priceDraft.max}
            onChange={(e) =>
              setPriceDraft((prev) => ({
                ...prev,
                max: e.target.value,
              }))
            }
            className="w-full min-w-0 rounded border border-[#eeeaf3] px-1.5 py-1 text-[10px] outline-none focus:border-[#7027df]"
            aria-label="Maximum price"
          />
        </div>

        <button
          type="button"
          onClick={commitPrice}
          className="mt-2 w-full rounded bg-[#7027df] py-1.5 text-[10px] font-semibold text-white hover:bg-[#5920b5]"
        >
          Apply price
        </button>
      </FilterSection>

      {/* Brand */}
      <FilterSection title="Brand">
        <div className="mb-2">
          <SearchInput
            value={brandSearch}
            onChange={(e) => setBrandSearch(e.target.value)}
            placeholder="Search brands..."
          />
        </div>

        {filteredBrands.length ? (
          <div className="max-h-40 overflow-y-auto">
            {filteredBrands.map((brand) => (
              <CheckOption
                key={brand}
                label={brand}
                checked={selected("brand").includes(brand)}
                onChange={() => toggleOption("brand", brand)}
              />
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-[#8e8798]">
            No brands available in loaded products.
          </p>
        )}
      </FilterSection>

      {/* Flavour */}
      <FilterSection title="Flavour">
        <div className="mb-2">
          <SearchInput
            value={flavorSearch}
            onChange={(e) => setFlavorSearch(e.target.value)}
            placeholder="Search flavours..."
          />
        </div>

        {filteredFlavors.length ? (
          <div className="max-h-40 overflow-y-auto">
            {filteredFlavors.map((flavor) => (
              <CheckOption
                key={flavor}
                label={flavor}
                checked={selected("flavor").includes(flavor)}
                onChange={() => toggleOption("flavor", flavor)}
              />
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-[#8e8798]">
            No flavours available in loaded products.
          </p>
        )}
      </FilterSection>

      {/* Sold By */}
      <FilterSection title="Sold By">
        <div className="mb-2">
          <SearchInput
            value={sellerSearch}
            onChange={(e) => setSellerSearch(e.target.value)}
            placeholder="Search sellers..."
          />
        </div>

        {filteredSellers.length ? (
          <div className="max-h-40 overflow-y-auto">
            {filteredSellers.map((seller) => (
           <CheckOption
  key={seller.id}
  label={seller.name}
  checked={selected("seller").includes(seller.id)}
  onChange={() => {
    const params = new URLSearchParams(searchParams);
    const currentSellers = params.getAll("seller");

    params.delete("seller");

    if (!currentSellers.includes(seller.name)) {
      currentSellers.forEach((value) => params.append("seller", value));
      params.append("seller", seller.name);
    } else {
      currentSellers
        .filter((value) => value !== seller.name)
        .forEach((value) => params.append("seller", value));
    }

    setSearchParams(params);
  }}
/>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-[#8e8798]">
            No sellers available in loaded products.
          </p>
        )}
      </FilterSection>


      {/* Offers */}
      <FilterSection title="Offers">
        <CheckOption
          label="On Sale"
          checked={searchParams.get("onSale") === "true"}
          onChange={() =>
            updateParams({
              onSale:
                searchParams.get("onSale") === "true" ? "" : "true",
            })
          }
        />
      </FilterSection>

    
    </aside>
  );
}

export default ShopSidebar;
